import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import Modal from '@mui/material/Modal';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { getCountries, getStates } from 'country-state-picker';
import { PiBuildingsLight as OrganizationIcon } from 'react-icons/pi';
import { IoClose as Close } from 'react-icons/io5';
import {
  IoPersonOutline as IdentityIcon,
  IoPeopleOutline as PeopleIcon,
  IoBriefcaseOutline as BusinessIcon,
  IoLocationOutline as LocationIcon,
  IoDocumentTextOutline as RemarksIcon,
} from 'react-icons/io5';
import { ReactComponent as Edit } from '@Assets/editIcon.svg';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Tooltip,
} from '@mui/material';
import BorderBlueButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderBlueButtonV2';
import BorderWhiteButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderWhiteButtonV2';
import ErrorHandler from '@Root/ErrorHandler';
import NameCardService from '@Helpers/URLHelpers/WeboutService/NameCardService';
import OrganizationCardService from '@Helpers/URLHelpers/WeboutService/OrganizationCardService';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import './OrganizationCardView.scss';

const INDUSTRIES = [
  'Agriculture, Forestry, Fishing, and Hunting','Crop Production','Animal Production',
  'Forestry and Logging','Fishing and Aquaculture','Mining and Quarrying',
  'Oil and Gas Extraction','Mining of Coal, Metal Ores, and Non-metallic Minerals',
  'Quarrying','Construction','Residential Construction','Non-Residential Construction',
  'Heavy and Civil Engineering Construction','Manufacturing','Food and Beverage',
  'Textiles, Apparel, and Leather','Wood Products','Chemicals','Pharmaceuticals',
  'Machinery','Electronics','Automotive','Aerospace and Defense','Utilities',
  'Electric Power Generation, Transmission, and Distribution','Natural Gas Distribution',
  'Water, Sewage, and Other Systems','Wholesale Trade','Merchant Wholesalers',
  'Agents and Brokers','Retail Trade','Motor Vehicle and Parts Dealers',
  'Furniture and Home Furnishings Stores','Electronics and Appliance Stores',
  'Clothing and Clothing Accessories Stores','General Merchandise Stores',
  'Food and Beverage Stores','Health and Personal Care Stores',
  'Transportation and Warehousing','Air Transportation','Rail Transportation',
  'Water Transportation','Truck Transportation','Warehousing and Storage',
  'Information','Publishing Industries','Motion Picture and Sound Recording Industries',
  'Broadcasting and Telecommunications','Data Processing, Hosting, and Related Services',
  'Finance and Insurance','Banking','Securities and Investment','Insurance',
  'Real Estate and Rental and Leasing','Real Estate','Rental and Leasing Services',
  'Professional, Scientific, and Technical Services','Legal Services',
  'Accounting and Bookkeeping Services','Architectural and Engineering Services',
  'Scientific Research and Development Services','Management of Companies and Enterprises',
  'Administrative and Support and Waste Management and Remediation Services',
  'Administrative and Support Services','Waste Management and Remediation Services',
  'Educational Services','Healthcare and Social Assistance',
  'Arts, Entertainment, and Recreation','Accommodation and Food Services',
  'Other Services (except Public Administration)','Public Administration',
];

export default function OrganizationCardView(props) {
  const [showLoader, setShowLoader] = useState(true);
  const [organizationCard, setOrganizationCard] = useState({});
  const [cursor, setCursor] = useState();
  const [name, setName] = useState('');
  const [editName, setEditName] = useState(false);
  const [editOwner, setEditOwner] = useState(false);
  const [editPoc, setEditPoc] = useState(false);
  const [editIndustry, setEditIndustry] = useState(false);
  const [editAddress, setEditAddress] = useState(false);
  const [editPinCode, setEditPinCode] = useState(false);
  const [editCountry, setEditCountry] = useState(false);
  const [editState, setEditState] = useState(false);
  const [editRemarks, setEditRemarks] = useState(false);
  const [address, setAddress] = useState('');
  const [orgState, setOrgState] = useState('');
  const [, setCountryCode] = useState('');
  const [states, setStates] = useState([]);
  const [orgCountry, setOrgCountry] = useState();
  const [orgIndustry, setOrgIndustry] = useState('');
  const [, setCountry] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [remarks, setRemarks] = useState('');
  const [nameCardList, setNameCardList] = useState([]);
  const [edited, setEdited] = useState(false);
  const [, setOwnerId] = useState();
  const [ownerNameCard, setOwnerNameCard] = useState();
  const [, setPocId] = useState();
  const [pocNameCard, setPocNameCard] = useState();
  const [ownerNameCardListLoading, setOwnerNameCardListLoading] = useState(false);
  const [pocNameCardListLoading, setPocNameCardListLoading] = useState(false);

  const countries = getCountries();
  const { currentProjectId } = useReduxProject();
  const defaultFilters = {
    nameCardFilter: {
      projectIdList: [currentProjectId],
      organizationCardIdList: [props.organizationCard?.id],
      sortBy: 'createTimeStamp',
      sortAsc: false,
    },
    page: 0,
    size: 10,
  };
  const [filters] = useState(defaultFilters);
  const errorHandler = new ErrorHandler();
  const organizationCardService = new OrganizationCardService();
  const nameCardService = new NameCardService();
  const cn = 'organizationCardView';

  useEffect(() => {
    parseData();
    renderData();
  }, [props]);

  const renderData = async () => {
    try {
      let nameCardListObj = await nameCardService.getListFiltered(filters);
      nameCardListObj = JSON.parse(nameCardListObj.body);
      setNameCardList(nameCardListObj?.result?.nameCardList || []);
      if (props.organizationCard.owner) {
        let ownerObj = await nameCardService.getById(props.organizationCard.owner);
        ownerObj = JSON.parse(ownerObj.body);
        setOwnerNameCard(ownerObj);
      }
      if (props.organizationCard.poc) {
        let pocObj = await nameCardService.getById(props.organizationCard.poc);
        pocObj = JSON.parse(pocObj.body);
        setPocNameCard(pocObj);
      }
    } catch (err) {
      errorHandler.handleError(err);
    }
    setShowLoader(false);
  };

  const parseData = () => {
    setImageUrl(
      props.organizationCard?.photo
        ? URL.createObjectURL(dataURLtoFile(props.organizationCard.photo))
        : ''
    );
    setOrganizationCard(props.organizationCard);
    setName(props.organizationCard?.name || '');
    setOwnerId(props.organizationCard?.owner);
    setPocId(props.organizationCard?.poc);
    setOrgIndustry(props.organizationCard?.industryType || '');
    setAddress(props.organizationCard?.address || '');
    setCountry(props.organizationCard?.country || '');
    setCountryCode(props.organizationCard?.countryCode || '');
    if (props.organizationCard?.countryCode) setStates(getStates(props.organizationCard.countryCode));
    setOrgState(props.organizationCard?.state || '');
    setOrgCountry({ name: props.organizationCard?.country || '', code: props.organizationCard?.countryCode || '' });
    setPinCode(props.organizationCard?.pinCode || '');
    setRemarks(props.organizationCard?.remarks || '');
  };

  const genericToggle = (flagSetter, flag, submit = true) => {
    if (flag && submit) handleSubmit();
    flagSetter(!flag);
  };

  const handleNameChange = (e) => {
    const sanitized = DOMPurify.sanitize(e.target.value);
    setName(sanitized);
    organizationCard.name = sanitized;
    setEdited(true);
  };

  const handleAddressChange = (e) => {
    const sanitized = DOMPurify.sanitize(e.target.value);
    setAddress(sanitized);
    organizationCard.address = sanitized;
    setEdited(true);
  };

  const handleRemarksChange = (e) => {
    const sanitized = DOMPurify.sanitize(e.target.value);
    setRemarks(sanitized);
    organizationCard.remarks = sanitized;
    setEdited(true);
  };

  const handleStateChange = (e, value) => {
    setOrgState(value || '');
    organizationCard.state = value || '';
    setEdited(true);
  };

  const handleOwnerTextChange = async (e) => {
    setOwnerNameCardListLoading(true);
    try {
      let filtersObj = { ...filters };
      filtersObj.nameCardFilter.searchQuery = e.target.value;
      let nameCardListObj = await nameCardService.getListFiltered(filtersObj);
      nameCardListObj = JSON.parse(nameCardListObj.body);
      setNameCardList(nameCardListObj?.result?.nameCardList || []);
    } catch (err) {
      errorHandler.handleError(err);
    }
    setOwnerNameCardListLoading(false);
  };

  const handlePocTextChange = async (e) => {
    setPocNameCardListLoading(true);
    try {
      let filtersObj = { ...filters };
      filtersObj.nameCardFilter.searchQuery = e.target.value;
      let nameCardListObj = await nameCardService.getListFiltered(filtersObj);
      nameCardListObj = JSON.parse(nameCardListObj.body);
      setNameCardList(nameCardListObj?.result?.nameCardList || []);
    } catch (err) {
      errorHandler.handleError(err);
    }
    setPocNameCardListLoading(false);
  };

  const handleOwnerChange = (e, value) => {
    if (!value) return;
    setOwnerId(value.id);
    setOwnerNameCard(value);
    organizationCard.owner = value.id;
    setEdited(true);
  };

  const handlePocChange = (e, value) => {
    if (!value) return;
    setPocId(value.id);
    setPocNameCard(value);
    organizationCard.poc = value.id;
    setEdited(true);
  };

  const handleIndustryChange = (e, value) => {
    setOrgIndustry(value || '');
    organizationCard.industryType = value || '';
    setEdited(true);
  };

  const handlePinCodeChange = (e) => {
    const re = /^[0-9\b]+$/;
    if (e.target.value === '' || re.test(e.target.value)) {
      setPinCode(e.target.value);
      organizationCard.pinCode = e.target.value;
      setEdited(true);
    }
  };

  const handleCountryChange = (e, value) => {
    if (!value) return;
    setStates(getStates(value.code));
    setCountry(value.name);
    setCountryCode(value.code);
    setOrgCountry(value);
    setOrgState('');
    organizationCard.country = value.name;
    organizationCard.countryCode = value.code;
    organizationCard.state = '';
    setEdited(true);
  };

  const handleFileChange = async (e) => {
    e?.preventDefault();
    const file = e.target.files?.[0];
    if (!file) return;
    if (validateImage(file)) {
      const base64File = await convertFileToBase64(file);
      setImageUrl(URL.createObjectURL(file));
      organizationCard.photo = base64File;
      setEdited(true);
      setUploadStatus('');
    } else {
      setUploadStatus('Invalid image');
    }
  };

  const validateImage = (file) => {
    if (file.type.split('/')[0] === 'image') {
      if (file.size > 1024 * 1024) { setUploadStatus('Image size exceeded 1MB!'); return false; }
      return true;
    }
    setUploadStatus('Upload image file!');
    return false;
  };

  const convertFileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const dataURLtoFile = (dataurl) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], 'file', { type: mime });
  };

  const handleSubmit = async (e) => {
    try {
      e?.preventDefault();
      setCursor('wait');
      await organizationCardService.updateOrganizationCard({ organizationCard });
      await new Promise((res) => setTimeout(res, 300));
      props.reloadData();
    } catch (err) {
      errorHandler.handleError(err);
    }
    setCursor('auto');
    props.closePopup();
  };

  // ─── Sub-components ──────────────────────────────────────────────────────────

  const Section = ({ title, icon, children }) => (
    <Box className={`${cn}__section`}>
      <Box className={`${cn}__section__head`}>
        {icon}
        <span>{title}</span>
      </Box>
      <Box className={`${cn}__section__body`}>
        {children}
      </Box>
    </Box>
  );

  const FieldRow = ({ label, editing, toggle, valueDisplay, error, children }) => (
    <Box className={`${cn}__fieldRow ${editing ? `${cn}__fieldRow--editing` : ''}`}>
      <Box className={`${cn}__fieldRow__labelCol`}>
        <Typography className={`${cn}__fieldRow__label`}>{label}</Typography>
      </Box>
      <Box className={`${cn}__fieldRow__valueCol`}>
        {editing ? (
          <Box className={`${cn}__fieldRow__input`} onBlur={toggle}>
            {children}
            {error && <Typography className={`${cn}__error`}>{error}</Typography>}
          </Box>
        ) : (
          <Typography
            className={`${cn}__fieldRow__value ${!valueDisplay ? `${cn}__muted` : ''}`}
            onClick={toggle}
          >
            {valueDisplay || '—'}
          </Typography>
        )}
      </Box>
      <Tooltip title={editing ? 'Save' : 'Edit'}>
        <IconButton size="small" onClick={toggle} className={`${cn}__editBtn`}>
          <Edit />
        </IconButton>
      </Tooltip>
    </Box>
  );

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <Modal open={props.open} onClose={props.closePopup} style={{ cursor }}>
      <div className={cn}>
        <div className={`${cn}__panel height-wfa`}>

          {/* ── Hero ─────────────────────────────────────────────────────── */}
          <Box className={`${cn}__hero`}>
            <Tooltip title="Change logo">
              <Box
                className={`${cn}__hero__photoWrap`}
                onClick={() => document.getElementById('fileInputOrgPhoto').click()}
              >
                <Avatar src={imageUrl} className={`${cn}__hero__avatar`} variant="rounded">
                  {!imageUrl && <OrganizationIcon className={`${cn}__hero__avatarIcon`} />}
                </Avatar>
                <Box className={`${cn}__hero__overlay`}>
                  <PhotoCameraIcon />
                </Box>
              </Box>
            </Tooltip>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              id="fileInputOrgPhoto"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <Typography className={`${cn}__hero__name`}>
              {name || 'Organization'}
            </Typography>
            {orgIndustry && (
              <Typography className={`${cn}__hero__meta`}>{orgIndustry}</Typography>
            )}
            {uploadStatus && (
              <Typography className={`${cn}__error ${cn}__error--hero`}>{uploadStatus}</Typography>
            )}
            <IconButton onClick={props.closePopup} className={`${cn}__closeBtn`}>
              <Close />
            </IconButton>
          </Box>

          {/* ── Form ─────────────────────────────────────────────────────── */}
          {showLoader ? (
            <Box className={`${cn}__loader`}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <form onSubmit={handleSubmit} className={`${cn}__form`}>

              <Section title="Identity" icon={<IdentityIcon />}>
                <FieldRow
                  label="Name"
                  editing={editName}
                  toggle={() => genericToggle(setEditName, editName)}
                  valueDisplay={name}
                >
                  <TextField
                    value={name}
                    onChange={handleNameChange}
                    size="small"
                    fullWidth
                    placeholder="Enter organization name"
                    inputProps={{ maxLength: 200 }}
                    autoFocus
                  />
                </FieldRow>
              </Section>

              <Section title="People" icon={<PeopleIcon />}>
                <FieldRow
                  label="Owner"
                  editing={editOwner}
                  toggle={() => genericToggle(setEditOwner, editOwner)}
                  valueDisplay={ownerNameCard?.name}
                >
                  <Autocomplete
                    options={nameCardList}
                    value={ownerNameCard || null}
                    onChange={handleOwnerChange}
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    onInputChange={handleOwnerTextChange}
                    loading={ownerNameCardListLoading}
                    renderInput={(p) => (
                      <TextField
                        {...p}
                        placeholder="Search contacts"
                        size="small"
                        InputProps={{
                          ...p.InputProps,
                          endAdornment: (
                            <>
                              {ownerNameCardListLoading && <CircularProgress size={14} style={{ marginRight: 6 }} />}
                              {p.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </FieldRow>
                <FieldRow
                  label="Point of Contact"
                  editing={editPoc}
                  toggle={() => genericToggle(setEditPoc, editPoc)}
                  valueDisplay={pocNameCard?.name}
                >
                  <Autocomplete
                    options={nameCardList}
                    value={pocNameCard || null}
                    onChange={handlePocChange}
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    onInputChange={handlePocTextChange}
                    loading={pocNameCardListLoading}
                    renderInput={(p) => (
                      <TextField
                        {...p}
                        placeholder="Search contacts"
                        size="small"
                        InputProps={{
                          ...p.InputProps,
                          endAdornment: (
                            <>
                              {pocNameCardListLoading && <CircularProgress size={14} style={{ marginRight: 6 }} />}
                              {p.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </FieldRow>
              </Section>

              <Section title="Business" icon={<BusinessIcon />}>
                <FieldRow
                  label="Industry"
                  editing={editIndustry}
                  toggle={() => genericToggle(setEditIndustry, editIndustry)}
                  valueDisplay={orgIndustry}
                >
                  <Autocomplete
                    options={INDUSTRIES}
                    value={orgIndustry || null}
                    onChange={handleIndustryChange}
                    getOptionLabel={(o) => o}
                    isOptionEqualToValue={(o, v) => o === v}
                    renderInput={(p) => <TextField {...p} placeholder="Select industry" size="small" />}
                  />
                </FieldRow>
              </Section>

              <Section title="Location" icon={<LocationIcon />}>
                <FieldRow
                  label="Address"
                  editing={editAddress}
                  toggle={() => genericToggle(setEditAddress, editAddress)}
                  valueDisplay={address}
                >
                  <TextField
                    value={address}
                    onChange={handleAddressChange}
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={4}
                    placeholder="Enter address"
                    autoFocus
                  />
                </FieldRow>
                <FieldRow
                  label="Country"
                  editing={editCountry}
                  toggle={() => genericToggle(setEditCountry, editCountry)}
                  valueDisplay={organizationCard?.country}
                >
                  <Autocomplete
                    options={countries}
                    value={orgCountry || null}
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(o, v) => o.code === v.code}
                    onChange={handleCountryChange}
                    renderInput={(p) => <TextField {...p} placeholder="Select country" size="small" />}
                  />
                </FieldRow>
                <FieldRow
                  label="State"
                  editing={editState}
                  toggle={() => genericToggle(setEditState, editState)}
                  valueDisplay={organizationCard?.state}
                >
                  <Autocomplete
                    options={states}
                    value={orgState || null}
                    getOptionLabel={(o) => o}
                    isOptionEqualToValue={(o, v) => o === v}
                    onChange={handleStateChange}
                    renderInput={(p) => <TextField {...p} placeholder="Select state" size="small" />}
                  />
                </FieldRow>
                <FieldRow
                  label="Pin Code"
                  editing={editPinCode}
                  toggle={() => genericToggle(setEditPinCode, editPinCode)}
                  valueDisplay={pinCode}
                >
                  <TextField
                    value={pinCode}
                    onChange={handlePinCodeChange}
                    size="small"
                    fullWidth
                    placeholder="Enter pin code"
                    inputProps={{ maxLength: 20 }}
                    autoFocus
                  />
                </FieldRow>
              </Section>

              <Section title="Notes" icon={<RemarksIcon />}>
                <FieldRow
                  label="Remarks"
                  editing={editRemarks}
                  toggle={() => genericToggle(setEditRemarks, editRemarks)}
                  valueDisplay={remarks}
                >
                  <TextField
                    value={remarks}
                    onChange={handleRemarksChange}
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    maxRows={6}
                    placeholder="Enter any remarks"
                    autoFocus
                  />
                </FieldRow>
              </Section>

            </form>
          )}

          {/* ── Actions ──────────────────────────────────────────────────── */}
          <Box className={`${cn}__actions`}>
            <BorderBlueButtonV2
              text="Update"
              onClick={handleSubmit}
              disabled={cursor === 'wait' || showLoader || !edited}
            />
            <BorderWhiteButtonV2
              text="Cancel"
              onClick={props.closePopup}
              disabled={cursor === 'wait'}
            />
          </Box>

        </div>
      </div>
    </Modal>
  );
}
